import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Divider from '@mui/material/Divider';
import DataItem from './ui/DataItem';
import { humanizeKey } from '../../utils/formatters/stringUtils';
import { formatValue } from '../../utils/formatters/valueUtils';

const THEMES_ORDER = [
  'uc',
  'aldeias',
  'florestas',
  'mineracao',
  'pra',
  'curso_dagua',
  'curso_dagua_trecho',
  'massa_dagua',
  'rios',
  'quilombolas',
  'assentamentos',
  'embargos_ibama',
  'terras_indigenas',
];

const THEME_LABELS = {
  uc: 'UC',
  aldeias: 'Aldeias',
  florestas: 'Florestas',
  mineracao: 'Mineração',
  pra: 'PRA',
  curso_dagua: "Cursos d'água",
  curso_dagua_trecho: "Cursos d'água - Trechos",
  massa_dagua: "Massas d'água",
  rios: 'Rios',
  quilombolas: 'Quilombolas',
  assentamentos: 'Assentamentos',
  embargos_ibama: 'Embargos IBAMA',
  terras_indigenas: 'Terras Indígenas',
};

function mergeByCodImovel(items) {
  const map = new Map();
  for (const it of items || []) {
    const cod = it?.cod_imovel || '—';
    const temas = it?.temas || {};
    const cur = map.get(cod) || { cod_imovel: cod, temas: {} };
    for (const key of THEMES_ORDER) {
      const arr = Array.isArray(temas?.[key]) ? temas[key] : [];
      const existing = Array.isArray(cur.temas[key]) ? cur.temas[key] : [];
      if (arr.length > 0) cur.temas[key] = [...existing, ...arr];
      else if (!cur.temas[key]) cur.temas[key] = [];
    }
    map.set(cod, cur);
  }
  return Array.from(map.values());
}

// Cartão no padrão SigefParcelCard: grade de DataItem, sem JSON cru
const ThemeRecordCard = ({ data, index, themeKey }) => {
  // Título heurístico leve (sem depender de campo específico)
  const title = `${THEME_LABELS[themeKey] || themeKey} • Item ${index + 1}`;
  const entries = Object.entries(data || {}).filter(
    ([key]) => key !== 'geojson' && key !== 'geometry' && key !== 'feature',
  );
  return (
    <div className="border rounded-lg border-gray-200 bg-white p-3 shadow-sm">
      <h5 className="font-medium text-slate-700 mb-2">{title}</h5>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {entries.map(([key, value]) => {
          const keyLower = String(key).toLowerCase();
          let display = formatValue(value);
          if (keyLower.startsWith('pct')) {
            const num =
              typeof value === 'number'
                ? value
                : value != null && !isNaN(value)
                  ? parseFloat(value)
                  : null;
            if (typeof num === 'number' && !isNaN(num)) {
              const pct = Math.abs(num) <= 1 ? num * 100 : num;
              display = `${pct.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
            }
          }
          return <DataItem key={key} label={humanizeKey(key)} value={display} />;
        })}
      </div>
    </div>
  );
};

const TemasTab = ({ propertyData }) => {
  const grupos = useMemo(() => {
    const arr = Array.isArray(propertyData?.properties?.sobreposicao)
      ? propertyData.properties.sobreposicao
      : Array.isArray(propertyData?.properties?.temas)
        ? propertyData.properties.temas
        : [];
    return mergeByCodImovel(arr);
  }, [propertyData]);

  if (!grupos || grupos.length === 0) {
    return (
      <div className="text-gray-500">
        Nenhum dado de Sobreposições disponível para esta propriedade.
      </div>
    );
  }

  return (
    <Box className="space-y-2">
      {grupos.map((grupo) => {
        const cod = grupo.cod_imovel || '—';
        const temas = grupo.temas || {};
        const enabledThemes = THEMES_ORDER.filter(
          (k) => Array.isArray(temas[k]) && temas[k].length > 0,
        );
        // Filtra grupos sem nenhum tema com dados (>0)
        if (enabledThemes.length === 0) return null;
        return (
          <Accordion key={cod} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 1,
                  width: '100%',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {cod}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {enabledThemes.length} grupos com dados
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box className="space-y-2">
                {enabledThemes.map((key) => (
                  <Accordion key={key} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {THEME_LABELS[key] || key}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box className="space-y-2">
                        {temas[key].map((item, idx) => (
                          <Box key={`${key}-${idx}`} className="space-y-1">
                            <ThemeRecordCard data={item} index={idx} themeKey={key} />
                            {idx < temas[key].length - 1 && <Divider sx={{ my: 1 }} />}
                          </Box>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default TemasTab;
